import { useEffect, useMemo, useRef, useState } from "react";
import RcSlider from "rc-slider";
import "rc-slider/assets/index.css";
import cx from "classnames";
import debounce from "lodash/debounce";
import produce from "immer";
import { BaseSelectableItem, ValidationResult } from "../../types/forms";
import { SliderScoreDisplayType } from "../../types/generic";
import ValidationWarning from "./ValidationWarning";
import FormRadioButtons from "./FormRadioButtons";
import {
  shouldRenderSliderAsRadioButtons,
  sliderThemeToRadioBackground,
  toFormRadioButtonOptions,
} from "../../helpers/sliderHelper";

export enum SliderTheme {
  WHITE = "WHITE",
  STANDARD = "STANDARD",
  THEMED = "THEMED",
}

interface SliderProps {
  /** The options to build the scale up with. Should have linear `value` properties, all numeric */
  scaleOptions: BaseSelectableItem[];
  /** Used for radio tile mode name/id attributes; required when multiple sliders appear on one page */
  uniqueFieldName?: string;
  /** An event to call when the input loses focus */
  onBlur?(latestValue: BaseSelectableItem[]): void | undefined;
  /** A function to call when the value changes (which could be each step along the slider as the user drags it) */
  onChange(newState: BaseSelectableItem[]): void;
  /** The theme to use for the slider */
  theme?: SliderTheme;
  /** Optional. To customise the top margin on the slider */
  topMarginClassName?: string;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  showNumericValue?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  isReadOnly?: boolean;
  /** Whether or not to show the numeric scale, or the selected items word, for the item selected in the slider */
  selectedValueDisplayMode: SliderScoreDisplayType;
}

const parseScaleValue = (value: BaseSelectableItem["value"]): number =>
  parseInt(value.toString(), 10);

const Slider = ({
  scaleOptions,
  uniqueFieldName,
  onChange,
  onBlur = undefined,
  selectedValueDisplayMode,
  showValidationErrors = false,
  validationResult = null,
  isReadOnly = false,
  theme = SliderTheme.STANDARD,
  topMarginClassName = "mt-5",
}: SliderProps) => {
  const [scalePoints, setScalePoints] = useState<BaseSelectableItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const debouncedHandleBlur = useMemo(
    () =>
      debounce((currentOptions: BaseSelectableItem[]) => {
        onBlurRef.current?.(currentOptions);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedHandleBlur.cancel();
    };
  }, [debouncedHandleBlur]);

  useEffect(() => {
    if (!scaleOptions) {
      setScalePoints([]);
      return;
    }

    setScalePoints(
      scaleOptions.map((option) => ({
        ...option,
        value: parseScaleValue(option.value),
      })),
    );
  }, [scaleOptions]);

  const handleSliderChange = (newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    const validPoints = scalePoints.map((point) => parseScaleValue(point.value));
    const nearestPoint = validPoints.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
    );

    const nextState = produce(scalePoints, (draft) => {
      draft.forEach((point) => {
        point.isSelected = parseScaleValue(point.value) === nearestPoint;
      });
    });
    onChange(nextState);
    debouncedHandleBlur(nextState);
  };

  const scaleExtent = useMemo(() => {
    if (scalePoints.length === 0) {
      return null;
    }

    const sliderMinOption = scalePoints.reduce((prev, curr) =>
      prev.value < curr.value ? prev : curr,
    );
    const sliderMaxOption = scalePoints.reduce((prev, curr) =>
      prev.value > curr.value ? prev : curr,
    );

    return { sliderMinOption, sliderMaxOption };
  }, [scalePoints]);

  if (!scalePoints.length || !scaleExtent) {
    return null;
  }

  const { sliderMinOption, sliderMaxOption } = scaleExtent;
  const renderAsRadioButtons = shouldRenderSliderAsRadioButtons(scalePoints);

  if (renderAsRadioButtons) {
    const fieldName =
      uniqueFieldName ??
      `slider_${scalePoints.map((point) => point.value).join("_")}`;

    return (
      <div className={topMarginClassName}>
        <FormRadioButtons
          uniqueFieldName={fieldName}
          values={toFormRadioButtonOptions(scalePoints)}
          onChange={onChange}
          onBlur={onBlur}
          showValidationErrors={showValidationErrors}
          validationResult={validationResult}
          isReadOnly={isReadOnly}
          background={sliderThemeToRadioBackground(theme)}
          containerClassNames=""
          maxTextLengthBeforeForcingVerticalLayout={30}
        />
      </div>
    );
  }

  const selectedScaleOption = scalePoints.find((point) => point.isSelected);
  const selectedValue = selectedScaleOption
    ? parseScaleValue(selectedScaleOption.value)
    : undefined;
  const selectedValueText = selectedScaleOption?.text ?? "";

  const defaultValue = parseScaleValue(
    scalePoints[Math.floor(scalePoints.length / 2)].value,
  );
  const sliderMin = parseScaleValue(sliderMinOption.value);
  const sliderMax = parseScaleValue(sliderMaxOption.value);
  const inputValue = selectedValue ?? defaultValue;
  const displayValue = selectedValue !== undefined ? selectedValue.toString() : "";

  const marks =
    sliderMax <= 10
      ? scalePoints.reduce(
          (acc, point) => {
            const value = parseScaleValue(point.value);
            acc[value] = point.text;
            return acc;
          },
          {} as Record<number, string>,
        )
      : undefined;

  const ariaValueTextFormatter = (value: number) => {
    const point = scalePoints.find(
      (scalePoint) => parseScaleValue(scalePoint.value) === value,
    );
    if (!point) {
      return String(value);
    }
    if (selectedValueDisplayMode === "WORD") {
      return point.text;
    }
    if (selectedValueDisplayMode === "NUMERIC") {
      return `${value} of ${sliderMax}`;
    }
    return point.text;
  };

  const isWhiteTheme = theme === SliderTheme.WHITE;
  const endpointLabelClassName = cx(
    "flex-auto w-1/3 text-xs",
    isWhiteTheme ? "text-white/80" : "text-gray-600",
  );
  const liveValueClassName = cx(
    "print:hidden font-medium tabular-nums",
    isWhiteTheme ? "text-white" : "text-gray-900",
  );
  const offEndpointClassName = cx(
    "flex-auto w-1/2 text-xs",
    isWhiteTheme ? "text-white/80" : "text-gray-600",
  );

  return (
    <div className={topMarginClassName}>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}
      <div
        className={cx(
          "relative w-full print:hidden mb-2 px-3",
          `rc-slider-theme-${theme.toLowerCase()}`,
          selectedValue === undefined && !isDragging && "rc-slider-centered",
          isReadOnly && "opacity-60",
        )}
      >
        <RcSlider
          id={uniqueFieldName}
          min={sliderMin}
          max={sliderMax}
          value={inputValue}
          onChange={handleSliderChange}
          onBeforeChange={() => setIsDragging(true)}
          onChangeComplete={() => setIsDragging(false)}
          disabled={isReadOnly}
          step={0.1}
          marks={marks}
          className="rc-slider-hide-mark-labels"
          ariaLabelForHandle="Rating scale"
          ariaValueTextFormatterForHandle={ariaValueTextFormatter}
        />
      </div>
      <div
        className={cx(
          "hidden print:block my-1 font-semibold text-sm text-center",
          isWhiteTheme ? "text-white" : "text-gray-900",
        )}
      >
        {selectedValueDisplayMode !== "WORD" && (
          <>{`${displayValue} / ${sliderMaxOption.value}`}</>
        )}
        {selectedValueDisplayMode === "WORD" && <>{selectedValueText}</>}
      </div>
      <div
        className="w-full flex mb-3 text-xs"
        aria-live="polite"
        aria-atomic="true"
      >
        {selectedValueDisplayMode === "NUMERIC" && (
          <>
            <span className={cx(endpointLabelClassName, "text-left")}>
              {sliderMinOption.text}
            </span>
            <span
              className={cx(
                "flex-auto w-1/3 text-center",
                isWhiteTheme ? "text-white/80" : "text-gray-600",
              )}
            >
              <span className={liveValueClassName}>
                {`${displayValue} / ${sliderMaxOption.value}`}
              </span>
            </span>
            <span className={cx(endpointLabelClassName, "text-right")}>
              {sliderMaxOption.text}
            </span>
          </>
        )}
        {selectedValueDisplayMode === "WORD" && (
          <>
            <span className={cx(endpointLabelClassName, "text-left italic")}>
              {sliderMinOption.text}
            </span>
            <span className="flex-auto w-1/3 text-center">
              <span className={cx(liveValueClassName, "print:hidden")}>
                {selectedValueText}
              </span>
            </span>
            <span className={cx(endpointLabelClassName, "text-right italic")}>
              {sliderMaxOption.text}
            </span>
          </>
        )}
        {selectedValueDisplayMode === "OFF" && (
          <>
            <span className={offEndpointClassName}>{sliderMinOption.text}</span>
            <span className={cx(offEndpointClassName, "text-right")}>
              {sliderMaxOption.text}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default Slider;
